import InputAdornment from '@mui/material/InputAdornment';
import { formatDimensionValueForDisplay } from '@src/common/components/FormatWeightValueForDisplay';
import { TextInput } from '@src/common/components/TextInput';
import {
  feetAndInchesToCm,
  metersToCm,
} from '@src/common/measurements/MeasurementUnits';
import { useMeasurement } from '@src/common/measurements/useMeasurement';
import type { ComponentProps } from 'react';
import { type ForwardedRef, forwardRef } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends Omit<
  ComponentProps<typeof TextInput>,
  'value' | 'onChange'
> {
  value: number | null;
  onChange: (value: number | null) => void;
  displayUnits?: boolean;
  ariaLabel?: string;
}

export const DimensionInput = forwardRef(function CustomNumberInput(
  {
    label = '',
    value,
    onChange,
    onBlur,
    ariaLabel,
    displayUnits = true,
    tabIndex,
    ...props
  }: Props,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const [isEditing, setIsEditing] = useState(false);

  const { isMetric } = useMeasurement();
  const { t } = useTranslation();
  const toDisplay = useCallback(
    (numberValue: number | undefined | null) =>
      formatDimensionValueForDisplay(numberValue, isMetric),
    [isMetric],
  );

  const [innerValue, setInnerValue] = useState<string>(toDisplay(value));

  useEffect(() => {
    if (!isEditing) {
      setInnerValue(toDisplay(value));
    }
  }, [isMetric, toDisplay, value, innerValue, isEditing]);

  const change = (newValue: string) => {
    setInnerValue(newValue);

    if (!newValue) {
      onChange(null);
      return;
    }
    if (isMetric) {
      if (new RegExp('^(\\d+([.]\\d{1,2})?|[.]\\d{0,2})$').test(newValue)) {
        onChange(toValue(newValue));
        return;
      }
    } else {
      if (
        new RegExp('^(\\d+([-]([0-9]|10|11))?|[-]([0-9]|10|11))$').test(
          newValue,
        )
      ) {
        onChange(toValue(newValue));
        return;
      }
    }
  };

  const toValue = (stringValue: string) => {
    if (isMetric) {
      return metersToCm(stringValue);
    } else {
      return feetAndInchesToCm(stringValue);
    }
  };

  return (
    <TextInput
      ref={ref}
      onFocus={() => {
        setIsEditing(true);
      }}
      slotProps={{
        htmlInput: {
          'aria-label': ariaLabel,
          tabIndex,
          readOnly: props.readOnly,
        },
        input: {
          startAdornment: displayUnits ? (
            <InputAdornment position="start">
              {isMetric ? t('units.meters') : t('units.feetAndInches')}
            </InputAdornment>
          ) : null,
        },
      }}
      label={label}
      value={innerValue}
      onBlur={(e) => {
        setInnerValue(toDisplay(value));
        setIsEditing(false);
        onBlur?.(e);
      }}
      onChange={(e) => change(e.target.value)}
      {...props}
    />
  );
});
